import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faRobot, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import DetailsEditor from './DetailsEditor';
import GitHubConnectionEdit from './GitHubConnectionEdit';
import useGithubSshConfigs from '../hooks/useGithubSshConfigs';
import { useTenants } from '../hooks/useTenants';
import { useGitHubInstallations } from '../hooks/useGitHubInstallations';
import GlossaryPanel from './GlossaryPanel';
import { useGlossary } from '../hooks/useGlossary';
import styles from './GitHubConfiguration.module.scss';
import '../styles/shared.scss';

interface GithubSshConfig {
    tenantId: string;
    name: string;
    description?: string;
    repositoryUrl: string;
    accessMethod: 'ssh';
    connectionType?: 'repository' | 'organization' | 'user';
    keyType?: 'rsa' | 'ed25519';
    isActive: boolean;
    secretArn?: string;
    publicKey: string;
    createdAt: string;
    updatedAt: string;
}

const GitHubConfiguration: React.FC = () => {
    const { data: connections = [], isLoading, error } = useGithubSshConfigs();
    const { data: installations = [] } = useGitHubInstallations();
    const { data: tenants = [] } = useTenants();
    const { isOpen, scrollToItem, openGlossary, closeGlossary } = useGlossary();
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createType, setCreateType] = useState<'ssh'>('ssh');
    const [selectedConnection, setSelectedConnection] = useState<GithubSshConfig | null>(null);
    const [sidebarWidth, setSidebarWidth] = useState(300);
    const isResizingRef = useRef(false);

    const getTenantName = (tenantId: string) => {
        const tenant = tenants.find(t => t.id === tenantId);
        return tenant?.name || tenantId;
    };

    const handleCreateConnection = () => {
        setSelectedConnection(null);
        setCreateType('ssh');
        setShowCreateForm(true);
    };

    const getAccessMethodIcon = (accessMethod: string) => {
        switch (accessMethod) {
            case 'github_app': return faRobot;
            case 'ssh': return faKey;
            default: return faGithub;
        }
    };

    const getKeyTypeSummary = (keyType?: string) => {
        return keyType ? keyType.toUpperCase() : 'SSH';
    };

    const handleConnectionSelect = (connection: GithubSshConfig) => {
        setSelectedConnection(connection);
        setShowCreateForm(false);
    };

    const handleFormComplete = () => {
        setShowCreateForm(false);
        setSelectedConnection(null);
    };

    const handleSave = () => {
        handleFormComplete();
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizingRef.current) return;
        const newWidth = Math.max(250, Math.min(600, e.clientX));
        setSidebarWidth(newWidth);
    }, []);

    const handleMouseUp = useCallback(() => {
        isResizingRef.current = false;
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        isResizingRef.current = true;
        e.preventDefault();
    };

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    return (
        <div className={styles.container}>
            {/* Left Column - Connections List */}
            <div 
                className={styles.sidebar} 
                style={{ width: `${sidebarWidth}px` }}
            >
                <div className={styles.header}>
                    <div className="page-title-group">
                        <h3>GitHub SSH Connections</h3>
                        <button
                            onClick={() => openGlossary('GitHub SSH Connection')}
                            className="info-button"
                            title="View definition"
                        >
                            Info
                        </button>
                    </div>
                    <button
                        onClick={handleCreateConnection}
                        className={`${styles.addButton} ${styles.ssh}`}
                        title="Add SSH Connection"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </button>
                </div>
                

                
                <div>
                    {isLoading ? (
                        <p className={styles.statusText}>
                            Loading connections...
                        </p>
                    ) : error ? (
                        <p className={styles.errorText}>
                            Error loading connections
                        </p>
                    ) : connections.length === 0 ? (
                        <p className={styles.statusText}>
                            No connections configured
                        </p>
                    ) : (
                        connections.map((connection) => (
                            <div
                                key={`${connection.tenantId}-${connection.repositoryUrl}`}
                                onClick={() => handleConnectionSelect(connection)}
                                className={`${styles.connectionItem} ${selectedConnection === connection ? styles.selected : ''}`}
                            >
                                <div className={styles.connectionHeader}>
                                    <FontAwesomeIcon 
                                        icon={getAccessMethodIcon(connection.accessMethod)} 
                                        className={styles.accessMethodIcon}
                                    />
                                    <div className={styles.connectionName}>
                                        {connection.name || connection.repositoryUrl.split('/').slice(-2).join('/')}
                                    </div>
                                    <div className={`${styles.status} ${connection.isActive ? styles.active : styles.inactive}`}>
                                        {connection.isActive ? 'Active' : 'Inactive'}
                                    </div>
                                </div>
                                <div className={styles.connectionMeta}>
                                    <div>{getTenantName(connection.tenantId)}</div>
                                    <div>{connection.accessMethod.toUpperCase()}</div>
                                    <div className={styles.keyType}>
                                        {getKeyTypeSummary(connection.keyType)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Resize Handle */}
            <div 
                className={styles.resizeHandle}
                onMouseDown={handleMouseDown}
            />

            {/* Right Column - Details/Form */}
            <div className={styles.content}>
                {showCreateForm ? (
                    <DetailsEditor
                        title="Create SSH Connection"
                        showDelete={false}
                        onComplete={handleFormComplete}
                    >
                        <GitHubConnectionEdit 
                            accessMethod={createType}
                            installations={installations}
                            onSave={handleSave} 
                            onCancel={handleFormComplete} 
                        />
                    </DetailsEditor>
                ) : selectedConnection ? (
                    <DetailsEditor
                        title="GitHub Connection Details"
                        onComplete={handleFormComplete}
                    >
                        <GitHubConnectionEdit 
                            connection={selectedConnection}
                            installations={installations}
                            onSave={handleSave}
                            onCancel={handleFormComplete}
                        />
                    </DetailsEditor>
                ) : (
                    <div className={styles.placeholder}>
                        Select an SSH connection or create a new one
                    </div>
                )}
            </div>
            
            <GlossaryPanel 
                isOpen={isOpen} 
                onClose={closeGlossary} 
                scrollToItem={scrollToItem} 
            />
        </div>
    );
};

export default GitHubConfiguration;